import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import VM from 'scratch-vm';

import uploadIcon from '../action-menu/icon--file-upload.svg';
import soundIcon from '../library-item/lib-icon--sound.svg';
import {costumeUpload, soundUpload} from '../../lib/file-uploader.js';
import {http} from '../../lib/http/request';

import styles from './user-center.css';

const PAGE_SIZE = 12;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_SOUND_SIZE = 10 * 1024 * 1024;
const IMAGE_ACCEPT = '.svg,.png,.bmp,.jpg,.jpeg,.gif';
const SOUND_ACCEPT = '.mp3,.wav,audio/mpeg,audio/wav,audio/wave,audio/x-wav,audio/x-pn-wav';

const MATERIAL_TYPES = [
    {
        id: 'BACKDROP',
        label: '背景',
        accept: IMAGE_ACCEPT,
        summary: '舞台背景'
    },
    {
        id: 'SPRITE',
        label: '精灵',
        accept: `${IMAGE_ACCEPT},${SOUND_ACCEPT}`,
        summary: '形象和声音'
    },
    {
        id: 'SOUND',
        label: '声音',
        accept: SOUND_ACCEPT,
        summary: '音频素材'
    },
    {
        id: 'COSTUME',
        label: '形象',
        accept: IMAGE_ACCEPT,
        summary: '角色造型'
    }
];

const IMAGE_EXTENSIONS = /\.(svg|png|bmp|jpg|jpeg|gif)$/i;
const SOUND_EXTENSIONS = /\.(mp3|wav)$/i;
const SOUND_MIME_TYPES = /^audio\/(mp3|mpeg|wav|wave|x-wav|x-pn-wav)$/i;

const MIME_BY_EXTENSION = {
    bmp: 'image/bmp',
    gif: 'image/gif',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    mp3: 'audio/mpeg',
    png: 'image/png',
    svg: 'image/svg+xml',
    wav: 'audio/wav'
};

const getTypeOption = type => MATERIAL_TYPES.find(item => item.id === type) || MATERIAL_TYPES[0];

const getBaseName = fileName => {
    const name = fileName || '';
    const dotIndex = name.indexOf('.');
    return dotIndex > 0 ? name.substring(0, dotIndex) : name;
};

const getFileType = file => {
    if (file.type) return file.type;
    const extensionMatch = String(file.name || '').match(/\.([^.]+)$/);
    const extension = extensionMatch ? extensionMatch[1].toLowerCase() : '';
    return MIME_BY_EXTENSION[extension] || '';
};

const toFileArray = fileList => Array.prototype.slice.call(fileList || []);

const getFileKey = file => [
    file && file.name,
    file && file.size,
    file && file.lastModified,
    file && file.type
].join('|');

const mergeFiles = (currentFiles, nextFiles) => {
    const existingKeys = new Set((currentFiles || []).map(getFileKey));
    return (currentFiles || []).concat((nextFiles || []).filter(file => {
        const key = getFileKey(file);
        if (existingKeys.has(key)) return false;
        existingKeys.add(key);
        return true;
    }));
};

const isImageFile = file => /^image\//.test(file.type) || IMAGE_EXTENSIONS.test(file.name);

const isSoundFile = file => SOUND_MIME_TYPES.test(file.type) || SOUND_EXTENSIONS.test(file.name);

const getSpriteFileGroups = files => files.reduce((groups, file) => {
    if (isImageFile(file)) {
        groups.costumes.push(file);
    } else if (isSoundFile(file)) {
        groups.sounds.push(file);
    } else {
        groups.unsupported.push(file);
    }
    return groups;
}, {
    costumes: [],
    sounds: [],
    unsupported: []
});

const formatDate = value => {
    if (!value) return '刚刚创建';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
};

const normalizeTags = tags => {
    if (Array.isArray(tags)) return tags;
    return String(tags || '')
        .split(',')
        .map(tag => tag.trim())
        .filter(Boolean);
};

const normalizeResource = item => {
    const resource = item || {};
    const dataFormat = resource.dataFormat || '';
    const md5ext = resource.md5ext || (
        resource.assetId && dataFormat ? `${resource.assetId}.${dataFormat}` : ''
    );
    return {
        ...resource,
        format: resource.format || dataFormat,
        tags: normalizeTags(resource.tags),
        md5ext
    };
};

const normalizeSprite = item => {
    const sprite = item || {};
    return {
        ...sprite,
        costumes: (Array.isArray(sprite.costumes) ? sprite.costumes : []).map(normalizeResource),
        sounds: (Array.isArray(sprite.sounds) ? sprite.sounds : []).map(normalizeResource),
        tags: normalizeTags(sprite.tags),
        type: 'SPRITE'
    };
};

const normalizePage = response => {
    const page = response && response.result ? response.result : {};
    const pageIndex = Number(page.current || 1);
    const pageSize = Number(page.size || PAGE_SIZE);
    const total = Number(page.total || 0);
    return {
        pageIndex,
        pageSize,
        records: Array.isArray(page.records) ? page.records : [],
        total,
        hasMore: total ? pageIndex * pageSize < total : false
    };
};

const getAssetUrl = item => {
    if (!item || !item.md5ext) return '';
    return `${http.domain}/internalapi/asset/${item.md5ext}/get/`;
};

const toUploadError = error => {
    if (error instanceof Error) return error;
    if (typeof error === 'string') return new Error(error);
    return new Error('素材解析失败');
};

const readArrayBuffer = file => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('文件读取失败'));
    reader.readAsArrayBuffer(file);
});

const validateFile = (file, type) => {
    if (!file) return '请选择素材文件';
    const isImage = isImageFile(file);
    const isSound = isSoundFile(file);

    if (type === 'SOUND') {
        if (!isSound) return '请选择 MP3 或 WAV 音频文件';
        if (file.size > MAX_SOUND_SIZE) return '声音文件不能超过 10MB';
        return '';
    }

    if (!isImage) return '请选择 SVG、PNG、JPG、BMP 或 GIF 图片';
    if (file.size > MAX_IMAGE_SIZE) return '图片文件不能超过 10MB';
    return '';
};

const validateSpriteFiles = files => {
    if (!files || files.length === 0) return '请选择精灵形象或声音文件';
    const groups = getSpriteFileGroups(files);
    if (groups.unsupported.length > 0) {
        return `${groups.unsupported[0].name || '文件'} 格式不支持`;
    }
    const oversizedImage = groups.costumes.find(file => file.size > MAX_IMAGE_SIZE);
    if (oversizedImage) return `${oversizedImage.name} 不能超过 10MB`;
    const oversizedSound = groups.sounds.find(file => file.size > MAX_SOUND_SIZE);
    if (oversizedSound) return `${oversizedSound.name} 不能超过 10MB`;
    if (groups.costumes.length === 0) return '精灵至少需要 1 个形象文件';
    return '';
};

const validateFiles = (files, type) => {
    if (type === 'SPRITE') return validateSpriteFiles(files);
    return validateFile(files && files[0], type);
};

const validateSpriteCostumeFiles = files => {
    if (!files || files.length === 0) return '请选择精灵形象文件';
    const invalidFile = files.find(file => !isImageFile(file));
    if (invalidFile) return `${invalidFile.name || '文件'} 不是支持的图片格式`;
    const oversizedFile = files.find(file => file.size > MAX_IMAGE_SIZE);
    if (oversizedFile) return `${oversizedFile.name} 不能超过 10MB`;
    return '';
};

const validateSpriteSoundFiles = files => {
    if (!files || files.length === 0) return '请选择精灵声音文件';
    const invalidFile = files.find(file => !isSoundFile(file));
    if (invalidFile) return `${invalidFile.name || '文件'} 不是支持的 MP3 或 WAV 音频`;
    const oversizedFile = files.find(file => file.size > MAX_SOUND_SIZE);
    if (oversizedFile) return `${oversizedFile.name} 不能超过 10MB`;
    return '';
};

const getSelectedFileLabel = (files, type) => {
    if (!files || files.length === 0) return '未选择文件';
    if (type !== 'SPRITE' || files.length === 1) return files[0].name;
    const groups = getSpriteFileGroups(files);
    return `已选择 ${files.length} 个文件：${groups.costumes.length} 个形象，${groups.sounds.length} 个声音`;
};

const getResourceNameFromFile = (file, fallback, index) => {
    const baseName = getBaseName(file && file.name).trim();
    const resourceName = baseName || `${fallback}${index ? index + 1 : ''}`;
    return resourceName.substring(0, 20);
};

class MaterialsPage extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'createImageResources',
            'createSoundResource',
            'createSpriteMaterial',
            'decodeSoundMetadata',
            'getImageMetadata',
            'handleDeleteMaterial',
            'handleFileChange',
            'handleLoadMore',
            'handleNameChange',
            'handlePickFile',
            'handlePickSpriteCostumeFiles',
            'handlePickSpriteSoundFiles',
            'handleRefresh',
            'handleRemoveSpriteFile',
            'handleSpriteCostumeFileChange',
            'handleSpriteSoundFileChange',
            'handleTypeSelect',
            'handleUpload',
            'loadMaterials',
            'renderMaterialCard',
            'renderSpriteFileChip',
            'renderSpriteFilePicker',
            'saveScratchResource',
            'saveScratchSprite',
            'setFileInput',
            'setSpriteCostumeInput',
            'setSpriteSoundInput',
            'storeAsset'
        ]);
        this.state = {
            activeType: 'BACKDROP',
            deletingId: '',
            error: '',
            files: [],
            hasMore: false,
            loading: false,
            materials: [],
            pageIndex: 1,
            pageSize: PAGE_SIZE,
            resourceName: '',
            total: 0,
            uploadStatus: '',
            uploadStatusType: 'info',
            uploading: false
        };
        this.fileInput = null;
        this.spriteCostumeInput = null;
        this.spriteSoundInput = null;
        this.mounted = false;
        this.requestId = 0;
    }
    componentDidMount () {
        this.mounted = true;
        this.loadMaterials(1);
    }
    componentWillUnmount () {
        this.mounted = false;
    }
    setFileInput (input) {
        this.fileInput = input;
    }
    setSpriteCostumeInput (input) {
        this.spriteCostumeInput = input;
    }
    setSpriteSoundInput (input) {
        this.spriteSoundInput = input;
    }
    getStorage () {
        return this.props.vm && this.props.vm.runtime && this.props.vm.runtime.storage;
    }
    resetFileInputs () {
        if (this.fileInput) this.fileInput.value = '';
        if (this.spriteCostumeInput) this.spriteCostumeInput.value = '';
        if (this.spriteSoundInput) this.spriteSoundInput.value = '';
    }
    handleTypeSelect (event) {
        const type = event.currentTarget.getAttribute('data-type');
        if (!type || type === this.state.activeType || this.state.uploading) return;
        this.setState({
            activeType: type,
            error: '',
            files: [],
            materials: [],
            resourceName: '',
            uploadStatus: '',
            uploadStatusType: 'info'
        }, () => {
            this.resetFileInputs();
            this.loadMaterials(1);
        });
    }
    handlePickFile () {
        if (this.fileInput && !this.state.uploading) {
            this.fileInput.click();
        }
    }
    handlePickSpriteCostumeFiles () {
        if (this.spriteCostumeInput && !this.state.uploading) {
            this.spriteCostumeInput.click();
        }
    }
    handlePickSpriteSoundFiles () {
        if (this.spriteSoundInput && !this.state.uploading) {
            this.spriteSoundInput.click();
        }
    }
    handleFileChange (event) {
        const files = toFileArray(event.currentTarget.files);
        if (files.length === 0) return;
        const validationMessage = validateFiles(files, this.state.activeType);
        if (validationMessage) {
            this.setState({
                files: [],
                uploadStatus: validationMessage,
                uploadStatusType: 'error'
            });
            event.currentTarget.value = '';
            return;
        }
        const groups = this.state.activeType === 'SPRITE' ? getSpriteFileGroups(files) : null;
        const nameSource = groups && groups.costumes.length > 0 ? groups.costumes[0] : files[0];
        this.setState({
            files,
            resourceName: getBaseName(nameSource.name).substring(0, 20),
            uploadStatus: '',
            uploadStatusType: 'info'
        });
    }
    handleSpriteCostumeFileChange (event) {
        const files = toFileArray(event.currentTarget.files);
        if (files.length === 0) return;
        const validationMessage = validateSpriteCostumeFiles(files);
        if (validationMessage) {
            this.setState({
                uploadStatus: validationMessage,
                uploadStatusType: 'error'
            });
            event.currentTarget.value = '';
            return;
        }
        this.setState(prevState => ({
            files: mergeFiles(prevState.files, files),
            resourceName: prevState.resourceName || getBaseName(files[0].name).substring(0, 20),
            uploadStatus: '',
            uploadStatusType: 'info'
        }));
        event.currentTarget.value = '';
    }
    handleSpriteSoundFileChange (event) {
        const files = toFileArray(event.currentTarget.files);
        if (files.length === 0) return;
        const validationMessage = validateSpriteSoundFiles(files);
        if (validationMessage) {
            this.setState({
                uploadStatus: validationMessage,
                uploadStatusType: 'error'
            });
            event.currentTarget.value = '';
            return;
        }
        this.setState(prevState => ({
            files: mergeFiles(prevState.files, files),
            resourceName: prevState.resourceName || getBaseName(files[0].name).substring(0, 20),
            uploadStatus: '',
            uploadStatusType: 'info'
        }));
        event.currentTarget.value = '';
    }
    handleRemoveSpriteFile (event) {
        const fileKey = event.currentTarget.getAttribute('data-file-key');
        if (!fileKey || this.state.uploading) return;
        this.setState(prevState => ({
            files: prevState.files.filter(file => getFileKey(file) !== fileKey),
            uploadStatus: '',
            uploadStatusType: 'info'
        }));
    }
    handleNameChange (event) {
        this.setState({
            resourceName: event.currentTarget.value,
            uploadStatus: '',
            uploadStatusType: 'info'
        });
    }
    handleUpload (event) {
        event.preventDefault();
        const files = this.state.files;
        const type = this.state.activeType;
        const name = this.state.resourceName.trim();
        const validationMessage = validateFiles(files, type);
        const storage = this.getStorage();

        if (validationMessage) {
            this.setState({
                uploadStatus: validationMessage,
                uploadStatusType: 'error'
            });
            return;
        }
        if (!name) {
            this.setState({
                uploadStatus: '请填写素材名称',
                uploadStatusType: 'error'
            });
            return;
        }
        if (!storage) {
            this.setState({
                uploadStatus: 'Scratch 存储未就绪',
                uploadStatusType: 'error'
            });
            return;
        }

        this.setState({
            uploadStatus: '正在上传素材...',
            uploadStatusType: 'info',
            uploading: true
        });

        const uploadTask = type === 'SPRITE' ?
            this.createSpriteMaterial(files, name) :
            readArrayBuffer(files[0])
                .then(buffer => {
                    if (type === 'SOUND') {
                        return this.createSoundResource(buffer, files[0], name);
                    }
                    return this.createImageResources(buffer, files[0], name, type);
                });

        uploadTask
            .then(() => {
                if (!this.mounted) return;
                this.setState({
                    files: [],
                    resourceName: '',
                    uploadStatus: '素材已保存',
                    uploadStatusType: 'success',
                    uploading: false
                }, () => {
                    this.resetFileInputs();
                    this.loadMaterials(1);
                });
            })
            .catch(error => {
                if (!this.mounted) return;
                this.setState({
                    uploadStatus: error.message || '素材上传失败',
                    uploadStatusType: 'error',
                    uploading: false
                });
            });
    }
    handleRefresh () {
        if (this.state.loading) return;
        this.loadMaterials(1);
    }
    handleLoadMore () {
        if (this.state.loading || !this.state.hasMore) return;
        this.loadMaterials(this.state.pageIndex + 1, true);
    }
    handleDeleteMaterial (event) {
        const materialId = event.currentTarget.getAttribute('data-material-id');
        const materialType = event.currentTarget.getAttribute('data-material-type');
        if (!materialId || this.state.deletingId) return;
        // eslint-disable-next-line no-alert
        if (!window.confirm('确定删除这个素材吗？')) return;

        const endpoint = materialType === 'SPRITE' ? '/scratchSprite/my' : '/scratchResource/my';
        this.setState({
            deletingId: materialId,
            uploadStatus: '',
            uploadStatusType: 'info'
        });
        http.delete(endpoint, {ids: materialId})
            .then(response => {
                if (response && response.success === false) {
                    throw new Error(response.message || '素材删除失败');
                }
                if (!this.mounted) return;
                this.setState({
                    deletingId: '',
                    uploadStatus: '素材已删除',
                    uploadStatusType: 'success'
                }, () => this.loadMaterials(1));
            })
            .catch(error => {
                if (!this.mounted) return;
                this.setState({
                    deletingId: '',
                    uploadStatus: error.message || '素材删除失败',
                    uploadStatusType: 'error'
                });
            });
    }
    loadMaterials (pageIndex, append) {
        const currentRequestId = this.requestId + 1;
        const activeType = this.state.activeType;
        const params = {
            pageIndex,
            pageSize: this.state.pageSize
        };
        this.requestId = currentRequestId;
        this.setState({
            error: '',
            loading: true
        });

        const request = activeType === 'SPRITE' ?
            http.get('/scratchSprite/myPageList', {
                ...params,
                isStage: false
            }) :
            http.get('/scratchResource/myPageList', {
                ...params,
                type: activeType
            });

        return request
            .then(response => {
                if (!this.mounted || currentRequestId !== this.requestId) return;
                if (response && response.success === false) {
                    throw new Error(response.message || '素材列表加载失败');
                }
                const page = normalizePage(response);
                const records = activeType === 'SPRITE' ?
                    page.records.map(normalizeSprite) :
                    page.records.map(normalizeResource).map(item => ({
                        ...item,
                        type: activeType
                    }));
                this.setState(prevState => ({
                    hasMore: page.hasMore,
                    materials: append ? prevState.materials.concat(records) : records,
                    pageIndex: page.pageIndex,
                    pageSize: page.pageSize,
                    total: page.total
                }));
            })
            .catch(error => {
                if (!this.mounted || currentRequestId !== this.requestId) return;
                this.setState(prevState => ({
                    error: error.message || '素材列表加载失败',
                    hasMore: false,
                    materials: append ? prevState.materials : [],
                    total: append ? prevState.total : 0
                }));
            })
            .then(() => {
                if (!this.mounted || currentRequestId !== this.requestId) return;
                this.setState({loading: false});
            });
    }
    storeAsset (vmAsset) {
        const storage = this.getStorage();
        const asset = vmAsset && vmAsset.asset;
        if (!storage || !asset) {
            return Promise.reject(new Error('Scratch 素材生成失败'));
        }
        return Promise.resolve(storage.store(
            asset.assetType,
            asset.dataFormat,
            asset.data,
            asset.assetId
        ));
    }
    getImageMetadata (vmAsset, type) {
        const fallbackCenter = type === 'BACKDROP' ?
            {rotationCenterX: 240, rotationCenterY: 180} :
            {rotationCenterX: 0, rotationCenterY: 0};
        const asset = vmAsset && vmAsset.asset;
        if (!asset || !asset.encodeDataURI || typeof Image === 'undefined') {
            return Promise.resolve({
                bitmapResolution: vmAsset.bitmapResolution || 1,
                ...fallbackCenter
            });
        }
        return new Promise(resolve => {
            const image = new Image();
            image.onload = () => {
                const width = image.naturalWidth || image.width || 0;
                const height = image.naturalHeight || image.height || 0;
                resolve({
                    bitmapResolution: vmAsset.bitmapResolution || 1,
                    rotationCenterX: width ? Math.round(width / 2) : fallbackCenter.rotationCenterX,
                    rotationCenterY: height ? Math.round(height / 2) : fallbackCenter.rotationCenterY
                });
            };
            image.onerror = () => {
                resolve({
                    bitmapResolution: vmAsset.bitmapResolution || 1,
                    ...fallbackCenter
                });
            };
            image.src = asset.encodeDataURI();
        });
    }
    decodeSoundMetadata (buffer) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return Promise.resolve({});
        const audioContext = new AudioContext();
        const closeAudioContext = () => {
            if (audioContext.close) {
                audioContext.close();
            }
        };

        return new Promise(resolve => {
            let settled = false;
            const finish = audioBuffer => {
                if (settled) return;
                settled = true;
                resolve({
                    rate: audioBuffer && audioBuffer.sampleRate ? Math.round(audioBuffer.sampleRate) : null,
                    sampleCount: audioBuffer && audioBuffer.length ? Math.round(audioBuffer.length) : null
                });
            };
            const fail = () => {
                if (settled) return;
                settled = true;
                resolve({});
            };

            try {
                const decodeResult = audioContext.decodeAudioData(buffer.slice(0));
                if (decodeResult && decodeResult.then) {
                    decodeResult.then(finish).catch(fail);
                    return;
                }
            } catch (e) {
                try {
                    audioContext.decodeAudioData(buffer.slice(0), finish, fail);
                    return;
                } catch (fallbackError) {
                    fail();
                    return;
                }
            }
            fail();
        }).then(metadata => {
            closeAudioContext();
            return metadata;
        });
    }
    createImageResources (buffer, file, name, type) {
        const storage = this.getStorage();
        return new Promise((resolve, reject) => {
            costumeUpload(buffer, getFileType(file), storage, resolve, error => reject(toUploadError(error)));
        }).then(vmCostumes => (
            Promise.all(vmCostumes.map((vmCostume, index) => {
                const resourceName = `${name}${index ? index + 1 : ''}`;
                vmCostume.name = resourceName;
                return this.storeAsset(vmCostume)
                    .then(() => this.getImageMetadata(vmCostume, type))
                    .then(metadata => this.saveScratchResource({
                        assetId: vmCostume.assetId,
                        bitmapResolution: metadata.bitmapResolution,
                        dataFormat: vmCostume.dataFormat,
                        md5ext: vmCostume.md5,
                        name: resourceName,
                        rotationCenterX: metadata.rotationCenterX,
                        rotationCenterY: metadata.rotationCenterY,
                        tags: [],
                        type
                    }));
            }))
        ));
    }
    createSoundResource (buffer, file, name) {
        const storage = this.getStorage();
        let newSound = null;
        return new Promise((resolve, reject) => {
            soundUpload(buffer, getFileType(file), storage, resolve, error => reject(toUploadError(error)));
        })
            .then(vmSound => {
                newSound = vmSound;
                newSound.name = name;
                return Promise.all([
                    this.storeAsset(newSound),
                    this.decodeSoundMetadata(buffer)
                ]);
            })
            .then(([, metadata]) => this.saveScratchResource({
                assetId: newSound.assetId,
                dataFormat: newSound.dataFormat,
                md5ext: newSound.md5,
                name,
                rate: metadata.rate,
                sampleCount: metadata.sampleCount,
                tags: [],
                type: 'SOUND'
            }));
    }
    createSpriteMaterial (files, name) {
        const groups = getSpriteFileGroups(files);
        const costumeTasks = groups.costumes.map((file, index) => (
            readArrayBuffer(file)
                .then(buffer => this.createImageResources(
                    buffer,
                    file,
                    getResourceNameFromFile(file, name, index),
                    'COSTUME'
                ))
        ));
        const soundTasks = groups.sounds.map((file, index) => (
            readArrayBuffer(file)
                .then(buffer => this.createSoundResource(
                    buffer,
                    file,
                    getResourceNameFromFile(file, name, index)
                ))
        ));

        return Promise.all([
            Promise.all(costumeTasks),
            Promise.all(soundTasks)
        ])
            .then(([costumeGroups, soundResources]) => {
                const costumeIds = costumeGroups
                    .reduce((ids, resources) => ids.concat(resources || []), [])
                    .map(resource => resource && resource.id)
                    .filter(Boolean);
                const soundIds = soundResources
                    .map(resource => resource && resource.id)
                    .filter(Boolean);
                if (costumeIds.length === 0) {
                    throw new Error('精灵形象保存失败');
                }
                return this.saveScratchSprite({
                    blocks: {},
                    costumes: costumeIds,
                    isStage: false,
                    name,
                    sounds: soundIds,
                    tags: [],
                    variables: {}
                });
            });
    }
    saveScratchResource (payload) {
        return http.post('/scratchResource', payload)
            .then(response => {
                if (response && response.success === false) {
                    throw new Error(response.message || '素材保存失败');
                }
                return response && response.result ? response.result : payload;
            });
    }
    saveScratchSprite (payload) {
        return http.post('/scratchSprite', payload)
            .then(response => {
                if (response && response.success === false) {
                    throw new Error(response.message || '精灵保存失败');
                }
                return response && response.result ? response.result : payload;
            });
    }
    renderTypeSelector () {
        return (
            <div className={styles.materialTypeList}>
                {MATERIAL_TYPES.map(type => {
                    const active = this.state.activeType === type.id;
                    const className = [
                        styles.materialTypeButton,
                        active ? styles.materialTypeButtonActive : ''
                    ].filter(Boolean).join(' ');
                    return (
                        <button
                            className={className}
                            data-type={type.id}
                            disabled={this.state.uploading}
                            key={type.id}
                            type="button"
                            onClick={this.handleTypeSelect}
                        >
                            <span className={styles.materialTypeName}>{type.label}</span>
                            <span className={styles.materialTypeSummary}>{type.summary}</span>
                        </button>
                    );
                })}
            </div>
        );
    }
    renderSpriteFileChip (file) {
        const fileTypeName = isImageFile(file) ? '形象' : '声音';
        const fileKey = getFileKey(file);
        return (
            <span
                className={styles.spriteFileChip}
                key={fileKey}
            >
                <span className={styles.spriteFileType}>{fileTypeName}</span>
                <span className={styles.spriteFileName}>{file.name}</span>
                <button
                    className={styles.spriteFileRemoveButton}
                    data-file-key={fileKey}
                    disabled={this.state.uploading}
                    type="button"
                    onClick={this.handleRemoveSpriteFile}
                >
                    {'移除'}
                </button>
            </span>
        );
    }
    renderSpriteFilePicker () {
        return (
            <div className={styles.materialFilePicker}>
                <div className={styles.materialFileActions}>
                    <button
                        className={styles.materialFileButton}
                        disabled={this.state.uploading}
                        type="button"
                        onClick={this.handlePickSpriteCostumeFiles}
                    >
                        <img
                            className={styles.uploadIcon}
                            src={uploadIcon}
                        />
                        <span>{'添加形象'}</span>
                    </button>
                    <button
                        className={[
                            styles.materialFileButton,
                            styles.materialSoundFileButton
                        ].filter(Boolean).join(' ')}
                        disabled={this.state.uploading}
                        type="button"
                        onClick={this.handlePickSpriteSoundFiles}
                    >
                        <img
                            className={styles.uploadIcon}
                            src={uploadIcon}
                        />
                        <span>{'添加声音'}</span>
                    </button>
                </div>
                <input
                    accept={IMAGE_ACCEPT}
                    aria-label="选择精灵形象文件"
                    className={styles.materialFileInput}
                    multiple
                    ref={this.setSpriteCostumeInput}
                    type="file"
                    onChange={this.handleSpriteCostumeFileChange}
                />
                <input
                    accept={SOUND_ACCEPT}
                    aria-label="选择精灵声音文件"
                    className={styles.materialFileInput}
                    multiple
                    ref={this.setSpriteSoundInput}
                    type="file"
                    onChange={this.handleSpriteSoundFileChange}
                />
                <div className={styles.materialFileName}>
                    {getSelectedFileLabel(this.state.files, this.state.activeType)}
                </div>
                {this.state.files.length > 0 ? (
                    <div className={styles.spriteFileList}>
                        {this.state.files.map(this.renderSpriteFileChip)}
                    </div>
                ) : null}
            </div>
        );
    }
    renderMaterialThumb (material) {
        if (material.type === 'SOUND') {
            return (
                <div className={styles.materialSoundThumb}>
                    <img src={soundIcon} />
                </div>
            );
        }
        const previewResource = material.type === 'SPRITE' ?
            material.costumes[0] :
            material;
        const previewUrl = getAssetUrl(previewResource);
        if (previewUrl) {
            return (
                <img
                    className={styles.materialThumbImage}
                    src={previewUrl}
                />
            );
        }
        return (
            <div className={styles.materialThumbFallback}>
                {(material.name || '素').charAt(0)}
            </div>
        );
    }
    renderMaterialCard (material) {
        const materialType = material.type || this.state.activeType;
        const option = getTypeOption(materialType);
        return (
            <article
                className={styles.materialCard}
                key={`${materialType}-${material.id}`}
            >
                <div className={styles.materialThumb}>
                    {this.renderMaterialThumb(material)}
                </div>
                <div className={styles.materialCardBody}>
                    <div className={styles.materialName}>{material.name || '未命名素材'}</div>
                    <div className={styles.materialMeta}>
                        {`${option.label} · ${formatDate(material.updateTime || material.createTime)}`}
                    </div>
                    <div className={styles.materialFormat}>
                        {materialType === 'SPRITE' ?
                            `${material.costumes.length} 个形象 · ${material.sounds.length} 个声音` :
                            (material.dataFormat || 'scratch')}
                    </div>
                    <button
                        className={styles.materialDeleteButton}
                        data-material-id={material.id}
                        data-material-type={materialType}
                        disabled={this.state.deletingId === material.id}
                        type="button"
                        onClick={this.handleDeleteMaterial}
                    >
                        {this.state.deletingId === material.id ? '删除中' : '删除'}
                    </button>
                </div>
            </article>
        );
    }
    render () {
        const activeOption = getTypeOption(this.state.activeType);
        const fileName = getSelectedFileLabel(this.state.files, this.state.activeType);
        const uploadStatusClass = [
            styles.materialUploadStatus,
            this.state.uploadStatusType === 'success' ? styles.statusSuccess : '',
            this.state.uploadStatusType === 'error' ? styles.statusError : ''
        ].filter(Boolean).join(' ');

        return (
            <section className={styles.materialsPage}>
                <div className={styles.materialsToolbar}>
                    <div>
                        <div className={styles.projectsTitle}>{'我的素材'}</div>
                        <div className={styles.projectsSummary}>
                            {this.state.loading ? '正在读取素材' : `共 ${this.state.total} 个${activeOption.label}素材`}
                        </div>
                    </div>
                    <button
                        className={styles.secondaryButton}
                        disabled={this.state.loading}
                        type="button"
                        onClick={this.handleRefresh}
                    >
                        {'刷新'}
                    </button>
                </div>
                {this.renderTypeSelector()}
                <form
                    className={styles.materialUploadPanel}
                    onSubmit={this.handleUpload}
                >
                    {this.state.activeType === 'SPRITE' ? this.renderSpriteFilePicker() : (
                        <div className={styles.materialFilePicker}>
                            <button
                                className={styles.materialFileButton}
                                disabled={this.state.uploading}
                                type="button"
                                onClick={this.handlePickFile}
                            >
                                <img
                                    className={styles.uploadIcon}
                                    src={uploadIcon}
                                />
                                <span>{'选择文件'}</span>
                            </button>
                            <input
                                accept={activeOption.accept}
                                aria-label="选择素材文件"
                                className={styles.materialFileInput}
                                ref={this.setFileInput}
                                type="file"
                                onChange={this.handleFileChange}
                            />
                            <div className={styles.materialFileName}>{fileName}</div>
                        </div>
                    )}
                    <label className={styles.materialNameField}>
                        <span className={styles.fieldLabel}>{'素材名称'}</span>
                        <input
                            className={styles.textInput}
                            disabled={this.state.uploading}
                            maxLength="20"
                            placeholder="填写素材名称"
                            value={this.state.resourceName}
                            onChange={this.handleNameChange}
                        />
                    </label>
                    <button
                        className={styles.primaryButton}
                        disabled={this.state.uploading}
                        type="submit"
                    >
                        {this.state.uploading ? '上传中' : '上传'}
                    </button>
                </form>
                <div className={uploadStatusClass}>{this.state.uploadStatus}</div>
                {this.state.error ? (
                    <div className={styles.projectsError}>
                        {this.state.error}
                    </div>
                ) : null}
                {!this.state.loading && !this.state.error && this.state.materials.length === 0 ? (
                    <div className={styles.emptyProjects}>
                        <div className={styles.emptyProjectsTitle}>{'暂无素材'}</div>
                        <div className={styles.emptyProjectsText}>{'上传后会在这里显示。'}</div>
                    </div>
                ) : null}
                {this.state.materials.length > 0 ? (
                    <div className={styles.materialGrid}>
                        {this.state.materials.map(this.renderMaterialCard)}
                    </div>
                ) : null}
                <div className={styles.loadMoreStatus}>
                    {this.state.loading ?
                        '正在加载' :
                        (this.state.materials.length > 0 ?
                            (this.state.hasMore ? '还有更多素材' : '没有更多素材了') :
                            '')}
                </div>
                {this.state.hasMore && !this.state.loading ? (
                    <button
                        className={styles.materialLoadMoreButton}
                        type="button"
                        onClick={this.handleLoadMore}
                    >
                        {'加载更多'}
                    </button>
                ) : null}
            </section>
        );
    }
}

MaterialsPage.propTypes = {
    vm: PropTypes.instanceOf(VM).isRequired
};

export default MaterialsPage;
